/*
 * Copyright Camunda Services GmbH and/or licensed to Camunda Services GmbH under
 * one or more contributor license agreements. See the NOTICE file distributed
 * with this work for additional information regarding copyright ownership.
 * Licensed under the Camunda License 1.0. You may not use this file
 * except in compliance with the Camunda License 1.0.
 */

import { FeelPlaygroundCollapsiblePanel } from 'features/properties-panel-feel-playground/playground-panel';
import { Status } from 'features/properties-panel-feel-playground/status';
import { formatWarningTitle, getTooltipMessage } from 'features/properties-panel-feel-playground/utils/helpers';

import {
  ResultContainer,
  ResultText,
  WarningTitle,
  WarningsList,
  Separator,
  ErrorTitle,
  ErrorText,
  EmptyStateText
} from './ResultViewerPanel.styled';

const RENDERED_STATES = ['success', 'error', 'loading', 'warning', 'unknown'];

const EmptyResult = () => (
  <ResultContainer>
    <EmptyStateText>Enter a valid FEEL expression and context to evaluate.</EmptyStateText>
  </ResultContainer>
);

const LoadingResult = () => (
  <ResultContainer>
    <div>Evaluating...</div>
  </ResultContainer>
);

/**
 * @param {Object} props
 * @param {string} props.error
 */
const UnknownResult = () => (
  <ResultContainer>{'Something went wrong during evaluation. Try again later or contact support.'}</ResultContainer>
);

/**
 * @param {Object} props
 * @param {string} props.error
 */
const ErrorResult = ({ error }) => (
  <ResultContainer>
    <ErrorTitle>Error</ErrorTitle>
    <ErrorText>{error}</ErrorText>
    <Separator />
  </ResultContainer>
);

/**
 * @param {Object} props
 * @param {any} props.result
 */
const SuccessResult = ({ result }) => (
  <ResultContainer>
    <ResultText>{JSON.stringify(result, null, 2)}</ResultText>
  </ResultContainer>
);

/**
 * @param {Object} props
 * @param {any} props.result
 * @param {Array<Warning>} [props.warnings]
 */
const WarningsResult = ({ result, warnings = [] }) => (
  <ResultContainer>
    {warnings.length > 0 && (
      <>
        <WarningsList>
          {warnings.map((warning) => (
            <li key={warning.message}>
              <WarningTitle>{formatWarningTitle(warning.type)}: </WarningTitle>
              <span>{warning.message}</span>
            </li>
          ))}
        </WarningsList>
        <Separator />
      </>
    )}
    <ResultText>{JSON.stringify(result, null, 2)}</ResultText>
  </ResultContainer>
);

/**
 * @typedef {Object} Warning
 * @property {string} type
 * @property {string} message
 */

/**
 * @typedef {Object} BaseState
 * @property {string} status
 */

/**
 * @typedef {Object} IdleState
 * @property {'idle'} status
 */

/**
 * @typedef {Object} SuccessState
 * @property {'success'} status
 * @property {any} result
 */

/**
 * @typedef {Object} ErrorState
 * @property {'error'} status
 * @property {string} error
 */

/**
 * @typedef {Object} LoadingState
 * @property {'loading'} status
 */

/**
 * @typedef {Object} UnknownState
 * @property {'unknown'} status
 * @property {string} error
 */

/**
 * @typedef {Object} WarningState
 * @property {'warning'} status
 * @property {any} result
 * @property {Array<Warning>} [warnings]
 */

/**
 * @typedef {IdleState | SuccessState | ErrorState | LoadingState | UnknownState | WarningState} ValidationState
 */

/**
 * @param {Object} props
 * @property {ValidationState} props.state
 * @property {string} props.expandedHeight
 * @property {boolean} props.isCollapsed
 * @property {() => void} props.onToggleCollapsed
 */
export const ResultViewerPanel = ({ state, expandedHeight, isCollapsed, onToggleCollapsed }) => {
  const content = (() => {
    switch (state.status) {
      case 'loading':
        return <LoadingResult />;
      case 'error':
        return <ErrorResult error={state.error} />;
      case 'warning':
        return <WarningsResult result={state.result} warnings={state.warnings} />;
      case 'success':
        return <SuccessResult result={state.result} />;
      case 'unknown':
        return <UnknownResult />;
      default:
        return <EmptyResult />;
    }
  })();

  const headerItems = RENDERED_STATES.includes(state.status) ? (
    <Status
      status={state.status}
      tooltipMessage={getTooltipMessage('result', state.status)}
      warningsCount={state.warnings?.length ?? 0}
    />
  ) : null;

  return (
    <FeelPlaygroundCollapsiblePanel
      title="Result"
      expandedHeight={expandedHeight}
      headerItems={headerItems}
      isCollapsed={isCollapsed}
      onToggleCollapsed={onToggleCollapsed}
    >
      {content}
    </FeelPlaygroundCollapsiblePanel>
  );
};
